/* The Link control: 2,562 fields in the in-scope surface use it.
 *
 * It is the single thing a person touches most in an ERP, so it is worth more
 * care than anything else here:
 *
 *   - it searches as you type, against Frappe's own search, so the results
 *     are the ones the desk would show and the DocType's `search_fields` are
 *     honoured;
 *   - it is a real combobox: arrow keys, Enter, Escape, and the ARIA roles a
 *     screen reader needs, because a picker you can only use with a mouse
 *     makes the whole form mouse-only;
 *   - it never blocks on the network. What you typed stays what you typed
 *     even if the search is slow or fails, and a failed search says so
 *     instead of pretending there are no matches.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import type { Control } from './kit'
import { Field, asText } from './kit'
import { useErp } from '../context'
import { QuickEntry } from '../QuickEntry'

type Option = { value: string; label?: string; description?: string }

/** The id `Field`'s label points at. */
const id = (name: string) => `f_${name}`

export const LinkControl: Control = (p) => {
  const target = p.field.options ?? ''
  return <LinkPicker {...p} target={target} />
}

/** Dynamic Link: the DocType comes from a sibling field named in `options`,
 *  and the picker has to re-point when that field changes. */
export const DynamicLinkControl: Control = (p) => {
  const pointerField = p.field.options ?? ''
  const target = asText((p.doc as Record<string, unknown>)[pointerField])
  return <LinkPicker {...p} target={target} />
}

function LinkPicker(p: Parameters<Control>[0] & { target: string }) {
  const erp = useErp()
  const listId = useId()
  const [open, setOpen] = useState(false)
  const [text, setText] = useState<string | null>(null)
  const [options, setOptions] = useState<Option[]>([])
  const [cursor, setCursor] = useState(0)
  const [searching, setSearching] = useState(false)
  const [failed, setFailed] = useState(false)
  const [creating, setCreating] = useState(false)
  const box = useRef<HTMLInputElement>(null)
  const wrap = useRef<HTMLDivElement>(null)

  const chosen = asText(p.value)
  const shown = text ?? chosen

  const filters = useMemo(() => {
    const raw = p.field.link_filters
    return raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : undefined
  }, [p.field.link_filters])

  useEffect(() => {
    if (!open || !p.target) return
    const controller = new AbortController()
    setSearching(true); setFailed(false)
    // Short debounce: long enough not to search on every keystroke, short
    // enough that it never feels like waiting.
    const timer = window.setTimeout(() => {
      erp.searchLink(p.target, text ?? '', filters, controller.signal)
        .then((r) => { setOptions(r.results ?? []); setCursor(0) })
        .catch((e) => { if ((e as Error).name !== 'AbortError') setFailed(true) })
        .finally(() => setSearching(false))
    }, 140)
    return () => { controller.abort(); window.clearTimeout(timer) }
  }, [open, text, p.target, erp, filters])

  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) { setOpen(false); setText(null) }
    }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [open])

  const pick = (option?: Option) => {
    if (!option) return
    p.onChange(option.value)
    setText(null); setOpen(false)
    box.current?.blur()
  }

  if (!p.target) {
    return (
      <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
        <input className="input" value={shown} readOnly placeholder="Choose the type first" />
      </Field>
    )
  }

  return (
    <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
      <div ref={wrap} style={{ position: 'relative' }}>
        <input
          // The label points at f_<fieldname>; without the id it labelled
          // nothing, so every Link field was nameless to a screen reader.
          id={id(p.field.fieldname)}
          ref={box} className="input" value={shown} readOnly={p.readOnly} required={p.required}
          autoFocus={p.autoFocus} autoComplete="off" spellCheck={false}
          role="combobox" aria-expanded={open} aria-controls={listId} aria-autocomplete="list"
          aria-invalid={p.error ? true : undefined}
          placeholder={`Search ${p.target}…`}
          onFocus={() => !p.readOnly && setOpen(true)}
          onChange={(e) => { setText(e.target.value); setOpen(true) }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setCursor((i) => Math.min(i + 1, options.length - 1)) }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((i) => Math.max(i - 1, 0)) }
            else if (e.key === 'Enter' && open) { e.preventDefault(); pick(options[cursor]) }
            else if (e.key === 'Escape') { setOpen(false); setText(null) }
            // Clearing the box clears the value, which is how somebody
            // removes a link without hunting for an x.
            else if ((e.key === 'Backspace' || e.key === 'Delete') && shown === '') p.onChange(null)
          }}
        />
        {chosen && !p.readOnly ? (
          <button type="button" className="btn btn-quiet btn-sm" aria-label={`Clear ${p.field.label ?? ''}`}
                  style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)' }}
                  onClick={() => { p.onChange(null); setText(null) }}>×</button>
        ) : null}

        {creating ? (
          <QuickEntry doctype={p.target} initialName={text ?? ''}
                      onCancel={() => setCreating(false)}
                      onDone={(made) => { setCreating(false); setText(null); p.onChange(made) }} />
        ) : null}
        {open ? (
          <div className="card" role="listbox" id={listId}
               style={{ position: 'absolute', zIndex: 30, insetInline: 0, top: 'calc(100% + 4px)',
                        boxShadow: 'var(--shadow-2)', maxHeight: 280, overflowY: 'auto', padding: 4 }}>
            {failed ? (
              <div className="small" style={{ padding: 10, color: 'var(--danger)' }}>
                Could not search just now. What you typed is kept.
              </div>
            ) : searching && options.length === 0 ? (
              <div className="small muted" style={{ padding: 10 }}>Searching…</div>
            ) : options.length === 0 ? (
              <div style={{ padding: 8 }}>
                <div className="small muted" style={{ padding: '2px 4px 8px' }}>
                  No {p.target} matches “{shown}”.
                </div>
                {/* The point at which people give up on an ERP: the thing
                    they need does not exist and making it means losing the
                    form they are halfway through. */}
                <button type="button" className="btn btn-sm btn-primary"
                        onMouseDown={(e) => { e.preventDefault(); setOpen(false); setCreating(true) }}>
                  Create a {p.target}
                </button>
              </div>
            ) : (
              options.map((o, i) => (
                <div
                  key={o.value} role="option" aria-selected={i === cursor}
                  className="navitem" style={i === cursor ? { background: 'var(--accent-soft)', color: 'var(--accent)' } : undefined}
                  onMouseEnter={() => setCursor(i)}
                  onMouseDown={(e) => { e.preventDefault(); pick(o) }}
                >
                  <span className="grow truncate">{o.label || o.value}</span>
                  {o.description ? <span className="small muted truncate" style={{ maxWidth: '45%' }}>{o.description}</span> : null}
                </div>
              ))
            )}
          </div>
        ) : null}
      </div>
    </Field>
  )
}
